import React, { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { Button } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  isOutlookAuthenticated,
  refreshOutlookToken,
} from "../../config/axiosOutlookConfig";
import { toast } from "react-toastify";
import { dashboardActions } from "../../store/dashboard/dashboardActions";

const OutlookLoginButton = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);
  const popupRef = useRef(null);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = isOutlookAuthenticated();
      setIsAuthenticated(authStatus);
    };

    checkAuth();

    // Check periodically
    const interval = setInterval(checkAuth, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // Try to refresh token on component mount if needed
  useEffect(() => {
    const tryRefreshOnMount = async () => {
      const token = localStorage.getItem("backend_api_token");
      if (token && !isOutlookAuthenticated()) {
        try {
          await refreshOutlookToken();
          setIsAuthenticated(true);
        } catch (error) {
          // Silent fail - user will need to login manually
        }
      }
    };

    tryRefreshOnMount();
  }, []);

  // Clean up popup on component unmount
  useEffect(() => {
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  const handleLogin = async () => {
    // Prevent multiple login attempts
    if (isLoginInProgress || loading) {
      toast.warning("Login is already in progress. Please wait.");
      return;
    }

    // ✅ ADD THIS: Check if we're already in a popup
    if (window.opener && window.location !== window.parent.location) {
      toast.error("Already in a popup window. Please close and try again.");
      return;
    }

    // Check if popup is already open
    if (popupRef.current && !popupRef.current.closed) {
      toast.warning(
        "Login window is already open. Please complete or close it first.",
      );
      popupRef.current.focus();
      return;
    }

    try {
      setIsLoginInProgress(true);
      setLoading(true);

      // Clear any existing popup state
      sessionStorage.removeItem("msal.login.error");
      sessionStorage.removeItem("msal.login.state");

      const loginResponse = await instance.loginPopup({
        // scopes: [
        //   "api://a5451d0f-f4c0-45c0-bf35-254c237be2ca/access_as_user",
        //   "User.Read",
        //   "Calendars.Read",
        //   "Calendars.ReadWrite",
        // ],
        scopes: [
          "api://ebcaf93b-866f-450d-86a0-2bf334053481/access_as_user",
          "User.Read",
          "Calendars.Read",
          "Calendars.ReadWrite",
        ],
        prompt: "select_account",
        redirectUri: window.location.origin,
      });

      if (loginResponse && loginResponse.account) {
        // Get token for backend API
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ["api://ebcaf93b-866f-450d-86a0-2bf334053481/access_as_user"],
          // scopes: ["api://a5451d0f-f4c0-45c0-bf35-254c237be2ca/access_as_user"], // FIXED: Changed : to -
          account: loginResponse.account,
        });

        // Store tokens
        const expiresIn = tokenResponse.expiresIn || 3600;
        const expiryTime = new Date().getTime() + expiresIn * 1000;

        localStorage.setItem("backend_api_token", tokenResponse.accessToken);
        localStorage.setItem("backend_api_token_expiry", expiryTime.toString());

        // Store user info
        const userInfo = {
          email: loginResponse.account.username,
          name: loginResponse.account.name,
        };
        localStorage.setItem("microsoft_user", JSON.stringify(userInfo));
        dispatch(dashboardActions.onChange("isOutlookLogin", true));
        setIsAuthenticated(true);
        // ✅ Close popup immediately after success
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
        toast.success("Outlook login successful!");
      }
    } catch (error) {
      console.error("Login error:", error);

      // Handle specific MSAL errors
      if (error.errorCode === "popup_window_error") {
        toast.error(
          "Popup was blocked or closed. Please allow popups for this site and try again.",
        );
      } else if (
        error.message &&
        error.message.includes("block_nested_popups")
      ) {
        toast.error(
          "A login window is already open. Please close it and try again.",
        );
        // Force close any stuck popup
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
          popupRef.current = null;
        }
      } else if (error.message && error.message.includes("user_cancelled")) {
        toast.info("Login was cancelled.");
      } else if (error.message && error.message.includes("popup_closed")) {
        toast.info("Login popup was closed. Please try again.");
      } else {
        toast.error(`Login failed: ${error.message || "Please try again"}`);
      }
    } finally {
      setLoading(false);
      setIsLoginInProgress(false);
      popupRef.current = null;
    }
  };

  const handleLogout = async () => {
    try {
      await instance.logoutPopup();

      // Clear all Microsoft auth data
      localStorage.removeItem("microsoft_user");
      localStorage.removeItem("backend_api_token");
      localStorage.removeItem("backend_api_token_expiry");
      localStorage.removeItem("id_token");

      setIsAuthenticated(false);
      dispatch(dashboardActions.onChange("isOutlookLogin", false));
      // Redirect to dashboard
      history.push("/dashboard");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Logout failed. Please try again.");
    }
  };

  // If authenticated, show Outlook menu
  if (isAuthenticated) {
    return (
      <>
        <Button
          onClick={handleLogout}
          style={{
            color: "#d13438",
            margin: "5px 0",
            width: "100%",
            textTransform: "none",
          }}>
          <span style={{ color: "#d13438" }}>Logout Outlook</span>
        </Button>
        <Button
          onClick={() => history.push("/calendarview")}
          style={{
            color: "#0078d4",
            margin: "5px 0",
            width: "100%",
            textTransform: "none",
          }}>
          <span style={{ color: "#0078d4" }}>Outlook Calendar</span>
        </Button>
        <Button
          onClick={() => history.push("/emailInbox")}
          style={{
            color: "#0078d4",
            margin: "5px 0",
            width: "100%",
            textTransform: "none",
          }}>
          <span style={{ color: "#0078d4" }}>Inbox Outlook</span>
        </Button>
      </>
    );
  }

  // Login button
  return (
    <Button
      onClick={() => handleLogin()}
      disabled={loading || isLoginInProgress}
      style={{
        color: "white",
        margin: "5px 0",
        width: "100%",
        textTransform: "none",
      }}>
      {loading || isLoginInProgress
        ? "Connecting to Outlook..."
        : "Login Outlook"}
    </Button>
  );
};

export default OutlookLoginButton;
